import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { formatSAR } from "@/i18n";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownLeft, Plus, CheckCircle2, Receipt, Smartphone, Landmark, Clock, Globe, Share2, Circle, Banknote } from "lucide-react";

export default function TransactionDetailModal({ tx, open, onClose }) {
  const { t, lang } = useApp();
  const [track, setTrack] = useState(null);

  useEffect(() => {
    setTrack(null);
    if (open && tx?.type === "intl_transfer") {
      api.get(`/remittance/${tx.tx_id}`).then((r) => setTrack(r.data)).catch(() => {});
    }
  }, [open, tx]);

  if (!tx) return null;

  const isIn = tx.direction === "in";
  const iconMap = { topup: Plus, bill: Receipt, recharge: Smartphone, bank_transfer: Landmark, intl_transfer: Globe, financing: Banknote, financing_repay: Banknote };
  const Icon = iconMap[tx.type] || (isIn ? ArrowDownLeft : ArrowUpRight);
  const typeLabel = { topup: t.type_topup, send: t.type_send, receive: t.type_receive, request_pay: t.type_request_pay, bill: t.type_bill, recharge: t.type_recharge, bank_transfer: t.type_bank_transfer, intl_transfer: t.type_intl_transfer, financing: t.type_financing, financing_repay: t.type_financing_repay }[tx.type] || tx.type;
  const isProcessing = tx.status === "processing";
  const date = new Date(tx.created_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
    day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

  const shareReceipt = async () => {
    const url = `${window.location.origin}/receipt/${track.reference}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "SbPay", text: `${t.remittanceReceipt} · ${track.reference}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t.receiptCopied);
      }
    } catch {}
  };

  const printReceipt = async () => {
    try {
      const { data: r } = await api.get(`/transactions/${tx.tx_id}/receipt`);
      const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
      const rows = [
        [t.reference, r.receipt_no], [t.date, new Date(r.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")],
        [t.txType, typeLabel], [isIn ? t.from : t.to, r.counterparty?.name ? `${r.counterparty.name}${r.counterparty.handle ? " · @" + r.counterparty.handle : ""}` : "—"],
        [t.note, r.note || "—"], [t.balanceAfter, r.balance_after != null ? `${formatSAR(r.balance_after, lang)} ${t.currency}` : "—"],
      ];
      const w = window.open("", "_blank", "noopener=no,width=480,height=720");
      if (!w) return;
      w.document.write(`<!doctype html><html dir="${t.dir}" lang="${lang}"><head><meta charset="utf-8"><title>${esc(r.receipt_no)}</title>
<style>body{font-family:system-ui,-apple-system,"Segoe UI",Tahoma,sans-serif;max-width:420px;margin:24px auto;padding:0 16px;color:#0f172a}
h1{font-size:18px;margin:0}.amt{font-size:32px;font-weight:800;margin:16px 0 4px}.muted{color:#64748b;font-size:12px}
table{width:100%;border-collapse:collapse;margin-top:16px}td{padding:10px 0;border-bottom:1px solid #e2e8f0;font-size:13px}td:last-child{text-align:end;font-weight:600}
.badge{display:inline-block;background:#fef3c7;color:#92400e;border-radius:99px;padding:2px 10px;font-size:11px;font-weight:700}</style></head><body>
<h1>SB Pay · ${esc(t.receipt)}</h1><div class="muted">${esc(r.issuer)}</div>
<div class="amt">${isIn ? "+" : "−"}${esc(formatSAR(r.amount, lang))} ${esc(t.currency)}</div>
${r.test_mode ? `<span class="badge">${esc(t.testMode)}</span>` : ""}
<table>${rows.map(([k, v]) => `<tr><td>${esc(k)}</td><td>${esc(v)}</td></tr>`).join("")}</table>
<p class="muted">${esc(r.account?.name)} · @${esc(r.account?.handle)} · ${esc(r.account?.wallet_number)}</p>
<script>window.onload=()=>window.print()</script></body></html>`);
      w.document.close();
    } catch {
      toast.error(t.errorGeneric);
    }
  };

  const Row = ({ label, value, testid }) => (
    <div className="flex items-center justify-between py-3 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span data-testid={testid} className="text-sm font-semibold text-end max-w-[60%] truncate">{value}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="transaction-detail-modal" className="max-w-sm rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center">{t.transactionDetails}</DialogTitle>
          <DialogDescription className="sr-only">{t.transactionDetails}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center py-2">
          <div className={`w-16 h-16 rounded-full grid place-items-center mb-3 ${isIn ? "bg-primary/15 text-primary" : "bg-muted text-foreground/70"}`}>
            <Icon className="w-8 h-8" />
          </div>
          <p className={`text-3xl font-black tabular-nums ${isIn ? "text-primary" : "text-foreground"}`}>
            {isIn ? "+" : "−"}{formatSAR(tx.amount, lang)}
            <span className="text-base font-bold ms-1">{t.currency}</span>
          </p>
          <div className={`flex items-center gap-1.5 mt-2 text-sm ${isProcessing ? "text-amber-500" : "text-primary"}`}>
            {isProcessing ? <Clock className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
            {isProcessing ? t.status_processing : t.completed}
          </div>
        </div>
        <div className="mt-2">
          <Row label={t.date} value={date} />
          <Row label={t.txType} value={typeLabel} />
          {tx.counterparty_name && tx.type !== "topup" && (
            <Row label={isIn ? t.from : t.to} value={tx.counterparty_handle ? `${tx.counterparty_name} · @${tx.counterparty_handle}` : tx.counterparty_name} testid="tx-detail-counterparty" />
          )}
          {tx.note && <Row label={t.note} value={tx.note} />}
          <Row label={t.reference} value={tx.reference || tx.tx_id} testid="tx-detail-reference" />
          {typeof tx.balance_after === "number" && <Row label={t.balanceAfter} value={`${formatSAR(tx.balance_after, lang)} ${t.currency}`} />}
          {tx.test_mode && <Row label={t.testMode} value="TEST" />}
        </div>
        <Button data-testid="print-receipt-btn" variant="outline" onClick={printReceipt} className="w-full h-11 rounded-2xl mt-2">
          <Receipt className="w-4 h-4 me-2" />{t.downloadReceipt}
        </Button>

        {track && (
          <div className="mt-3" data-testid="remittance-tracking">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold flex items-center gap-1.5"><Globe className="w-4 h-4 text-primary" />{t.trackTransfer}</h4>
              <span className="text-xs font-mono text-muted-foreground">{track.reference}</span>
            </div>
            <div className="rounded-2xl bg-muted/50 border border-border p-3 mb-3 text-sm flex items-center justify-between">
              <span className="text-muted-foreground">{t.recipientGets}</span>
              <span className="font-bold tabular-nums text-primary">{formatSAR(track.converted, lang)} {track.currency}</span>
            </div>
            <ol className="relative ms-2">
              {track.steps.map((s, i) => {
                const last = i === track.steps.length - 1;
                return (
                  <li key={s.key} data-testid={`track-step-${s.key}`} className="flex gap-3 pb-4 last:pb-0 relative">
                    {!last && <span className={`absolute start-[9px] top-5 bottom-0 w-0.5 ${s.done ? "bg-primary" : "bg-border"}`} />}
                    {s.done ? <CheckCircle2 className="w-5 h-5 text-primary shrink-0 z-10" /> : <Circle className="w-5 h-5 text-muted-foreground/40 shrink-0 z-10" />}
                    <div className="flex-1 -mt-0.5">
                      <p className={`text-sm font-semibold ${s.done ? "text-foreground" : "text-muted-foreground"}`}>{t[`step_${s.key}`]}</p>
                      <p className="text-xs text-muted-foreground">{new Date(s.at).toLocaleTimeString(lang === "ar" ? "ar-SA" : "en-US", { hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
            <Button data-testid="share-receipt-btn" onClick={shareReceipt} variant="outline" className="w-full h-11 rounded-2xl mt-2">
              <Share2 className="w-4 h-4 me-2" />{t.shareReceipt}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
